import { html } from 'lit';
import { faLink, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { t } from '@lingui/macro';
import classNames from 'classnames';
import { getFAIcon, generateEnhancedShareUrl } from '../../masonry-utils';
import { getMediaType } from '../../../utils';
import { AnalyticsEvents, AnalyticsScores, ShareNetworks, EmbedTypes } from '../../../consts';
import ShareBtn from './ShareBtn';

class CopyShareBtn extends ShareBtn {
  static get properties() {
    return {
      showConfirmation: { type: Boolean },
      post: { type: Object },
      postIndex: { type: Number },
    };
  }

  constructor() {
    super();
    this.showConfirmation = false;
    this.timeoutInstance = null;
  }

  clickHandler = async () => {
    try {
      const shareUrl = this.post ? generateEnhancedShareUrl(this.post) : window.location.href;
      await navigator.clipboard.writeText(shareUrl);
      this.showConfirmation = true;

      // Log analytics only for specific post shares (not general page shares)
      // This ensures we track user engagement with individual content pieces
      if (this.post && window.giggAnalytics) {
        giggAnalytics.logEvent(AnalyticsEvents.SHARE_POST, {
          // Board context data - identifies where the share occurred
          boardId: __gigg.accountId,
          boardType: EmbedTypes.MASONRY,
          folderId: __gigg.folderId,

          // Post-specific data - tracks what content was shared
          postIndex: this.postIndex || 0,
          postId: this.post.id,
          postType: this.post.service,
          mediaType: getMediaType(this.post.media),
          isCarousel: this.post.media?.length > 1,

          // Share method and scoring - tracks how content was shared and weights engagement
          shareMethod: ShareNetworks.LINK,
          scoreWeight: AnalyticsScores.SHARE_POST,

          // User journey tracking - connects this action to user's session timeline
          firstEvent: {
            _id: window.giggFirstEvent?._id,
            createdDate: window.giggFirstEvent?.createdDate,
          },
          inViewEvent: {
            _id: window.latestViewEvent?._id,
            createdDate: window.latestViewEvent?.createdDate,
          },
        });
      }
    } catch (err) {
      this.showConfirmation = false;
    }

    // Clear any existing timeout to prevent multiple timeouts
    if (this.timeoutInstance) {
      window.clearTimeout(this.timeoutInstance);
    }

    // Enhanced timeout behavior matching ModalShareButtons
    this.timeoutInstance = window.setTimeout(() => {
      this.showConfirmation = false;
    }, 4000);
  };

  /**
   * Cleanup when component is disconnected
   */
  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this.timeoutInstance) {
      window.clearTimeout(this.timeoutInstance);
    }
  }

  render() {
    return html`
      <div
        class=${classNames('web-share__confirm', {
          'web-share__confirm--active': this.showConfirmation,
        })}
      >
        ${getFAIcon(faCheckCircle)} ${t`Link copied to clipboard`}
      </div>

      <button class="web-share__btn" @click=${this.clickHandler}>${getFAIcon(faLink)}</button>
    `;
  }
}

export default CopyShareBtn;
